import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, FilePlus2 } from 'lucide-react';
import { PARTY_ROLE, PRODUCT_LINE } from '../../lib/labels';
import { Badge, Banner, Button, Textarea, useToast } from '../ui';
import type { AssistantDraft, QuotePrefill } from '../../pages/staff/AssistantPage';

/**
 * The draft bid from "Draft a bid from an email": the project and firms, the product and the wall, a budget, the
 * Circdal rules it may run into, what is missing, a reply asking for it and the custom quote's title and description.
 * "Start a custom quote from this" opens New custom quote filled in for staff to check. The reply is copied and sent
 * from staff's own email; the assistant never sends it.
 */
export function DraftCard({ draft, onNavigate }: { draft: AssistantDraft; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [reply, setReply] = useState(draft.replyDraft);

  const startQuote = () => {
    const prefill: QuotePrefill = {
      projectName: draft.projectName ?? undefined,
      city: draft.city ?? undefined,
      state: draft.state ?? undefined,
      title: draft.quoteTitle,
      description: [draft.quoteDescription, draft.missing.length ? `Still needed: ${draft.missing.join(' ')}` : ''].filter(Boolean).join('\n\n'),
      productLine: draft.productLine,
      wallWidthFt: draft.wall.widthFt ?? undefined,
      wallHeightFt: draft.wall.heightFt ?? undefined,
      budget: draft.budget ? { flatSqFt: draft.budget.flatSqFt, relief: draft.budget.relief } : null,
    };
    onNavigate?.();
    navigate('/quotes/custom', { state: { prefill } });
  };

  const wall = [
    draft.wall.widthFt ? `${draft.wall.widthFt} ft long` : null,
    draft.wall.heightFt ? `${draft.wall.heightFt} ft high` : null,
    draft.wall.plan,
    draft.wall.reliefInches ? `about ${draft.wall.reliefInches} in deep` : null,
    draft.wall.belowLitCove ? 'below a lit cove' : null,
    draft.wall.wrapsWallEnd ? 'wraps a wall end' : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="ap-draft" aria-label="Draft bid">
      <div className="ap-draft-head">Draft bid</div>
      <dl className="kv">
        <dt>Project</dt>
        <dd>{[draft.projectName, [draft.city, draft.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || 'Not stated'}</dd>
        <dt>Product</dt>
        <dd>{PRODUCT_LINE[draft.productLine] ?? draft.productLine}</dd>
        <dt>Wall</dt>
        <dd>{wall || 'Not stated'}</dd>
        {draft.budget ? (
          <>
            <dt>Budget</dt>
            <dd>
              {draft.budget.flatSqFt} SF of wall, {draft.budget.relief} relief{draft.budget.note ? `. ${draft.budget.note}` : ''}
            </dd>
          </>
        ) : null}
      </dl>
      {draft.firms.length ? (
        <div className="ap-firms">
          {draft.firms.map((f) => (
            <div key={f.name}>
              <b>{f.name}</b> {f.isClient ? <Badge tone="green">Client</Badge> : null}{' '}
              <span className="tiny muted">
                {PARTY_ROLE[f.role] ?? f.role}
                {f.people?.length ? ` · ${f.people.join(', ')}` : ''}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {draft.rulesNotes.length ? (
        <Banner tone="info">
          <b>Circdal rules to watch</b>
          <ul className="ap-list">
            {draft.rulesNotes.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </Banner>
      ) : null}
      {draft.missing.length ? (
        <div>
          <b>Still needed</b>
          <ul className="ap-list">
            {draft.missing.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <label className="small" style={{ fontWeight: 500 }}>
        Reply asking for what is missing
        <Textarea aria-label="Reply asking for what is missing" rows={6} value={reply} onChange={(e) => setReply(e.target.value)} />
      </label>
      <div className="row wrap">
        <Button variant="primary" size="sm" icon={<FilePlus2 />} onClick={startQuote}>
          Start a custom quote from this
        </Button>
        <Button
          size="sm"
          icon={<Copy />}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(reply);
              toast('The reply is copied. Send it from your own email.');
            } catch {
              toast('Select the reply and copy it.');
            }
          }}
        >
          Copy reply
        </Button>
      </div>
    </div>
  );
}
